import React, { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { DEV_TOOL_ICON_MAP } from "../data/devToolIcons";
import TextToSpeechTool from "./TextToSpeechTool";
import VoiceCloningTool from "./VoiceCloningTool";
import MusicGenerationTool from "./MusicGenerationTool";
import TranscriptionTool from "./TranscriptionTool";
import NoiseRemovalTool from "./NoiseRemovalTool";
import DocumentSummarizerTool from "./DocumentSummarizerTool";
import ContentGeneratorTool from "./ContentGeneratorTool";
import DocumentTranslatorTool from "./DocumentTranslatorTool";
import PdfChatTool from "./PdfChatTool";
import ResumeBuilderTool from "./ResumeBuilderTool";
import ImageGeneratorTool from "./ImageGeneratorTool";
import ImageEnhancerTool from "./ImageEnhancerTool";
import BackgroundRemovalTool from "./BackgroundRemovalTool";
import ImageEditorTool from "./ImageEditorTool";
import LogoGeneratorTool from "./LogoGeneratorTool";
import VideoGeneratorTool from "./VideoGeneratorTool";
import ImageVideoTool from "./ImageVideoTool";
import AvatarVideoTool from "./AvatarVideoTool";
import VideoScriptTool from "./VideoScriptTool";

// Registry mapping a tool's slug to its workspace component - add an entry here as each tool
// (from the spec: "rest all tools will be generated later one by one") gets built.
const TOOL_WORKSPACES = {
  "ai-text-to-speech-voice-generation": TextToSpeechTool,
  "ai-voice-cloning": VoiceCloningTool,
  "ai-music-generation": MusicGenerationTool,
  "ai-audio-and-podcast-transcription": TranscriptionTool,
  "ai-noise-removal-and-audio-enhancement": NoiseRemovalTool,
  "ai-document-summarization": DocumentSummarizerTool,
  "ai-text-and-content-generation": ContentGeneratorTool,
  "ai-document-translation": DocumentTranslatorTool,
  "ai-pdf-qanda-chatbot": PdfChatTool,
  "ai-resume-and-cv-builder": ResumeBuilderTool,
  "text-to-image-generation": ImageGeneratorTool,
  "ai-image-upscaling-and-enhancement": ImageEnhancerTool,
  "ai-background-removal": BackgroundRemovalTool,
  "ai-image-to-image-editing": ImageEditorTool,
  "ai-logo-and-design-generator": LogoGeneratorTool,
  "text-to-video-generation": VideoGeneratorTool,
  "image-to-video-generation": ImageVideoTool,
  "ai-avatar-and-talking-head-videos": AvatarVideoTool,
  "ai-video-script-generator": VideoScriptTool,
};

// An AI Tools category page (e.g. AI Document Tools): top container is the category's own banner
// plus a button for each of its individual tools (AI Document Summarization, ...); bottom
// container is an empty workspace reserved for that tool's generator UI - built one tool at a
// time later. Selecting a tool is local to this page (no navigation) since the workspace below
// reacts to whichever tool is picked. A direct link to a specific tool (?tool=<slug>, e.g. from
// the navbar dropdown) pre-selects that one.
export default function AIToolCategoryWorkspace({ category, tools }) {
  const [searchParams] = useSearchParams();
  const [selectedId, setSelectedId] = useState(tools[0]?.id ?? null);
  const [typedTitle, setTypedTitle] = useState("");

  useEffect(() => {
    const requested = searchParams.get("tool");
    const match = requested && tools.find((t) => t.slug === requested);
    setSelectedId(match ? match.id : tools[0]?.id ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tools]);

  useEffect(() => {
    const text = category.title || "";
    setTypedTitle("");
    let i = 0;
    const typeInterval = setInterval(() => {
      i += 1;
      setTypedTitle(text.slice(0, i));
      if (i >= text.length) clearInterval(typeInterval);
    }, 70);
    return () => clearInterval(typeInterval);
  }, [category.title]);

  useEffect(() => {
    document.title = `${category.title} - Mr. Aali Online Technologies`;
  }, [category.title]);

  const icons = (category.icons || "").split(",").filter(Boolean);
  const selectedTool = tools.find((t) => t.id === selectedId);

  return (
    <>
      {/* Top container: category banner + a button per tool */}
      <section className="relative py-10 text-white border-b border-navy-border bg-navy overflow-hidden">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="floating-light absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-accent/20 blur-[120px]" style={{ animationDuration: "18s" }} />
          <div className="floating-light absolute -bottom-40 -right-20 w-[500px] h-[500px] rounded-full bg-accent-light/10 blur-[120px]" style={{ animationDuration: "22s", animationDelay: "2s" }} />
          <div className="floating-light absolute top-1/3 left-1/2 w-[350px] h-[350px] rounded-full bg-purple-500/10 blur-[100px]" style={{ animationDuration: "26s", animationDelay: "4s" }} />
          <div className="floating-light absolute bottom-10 left-10 w-[300px] h-[300px] rounded-full bg-accent-light/10 blur-[100px]" style={{ animationDuration: "20s", animationDelay: "1s" }} />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <Link to="/ai-tools" className="inline-block text-accent-light text-sm mb-4 hover:underline">
            &larr; Back to AI Tools
          </Link>

          <div className="grid md:grid-cols-[30%_70%] gap-8 items-start">
            <div className="rounded-xl overflow-hidden bg-navy-dark border border-accent-light/25 shadow-[0_0_20px_rgba(34,211,238,0.2)]">
              {category.image ? (
                <img src={category.image} alt={category.title} className="w-full aspect-square object-cover bg-navy" />
              ) : (
                <div className="w-full aspect-square bg-brand-gradient" />
              )}
            </div>

            <div>
              <h1 className="text-2xl md:text-4xl font-extrabold tracking-tight">
                {typedTitle}
                <span className="typing-cursor inline-block w-[3px] ml-1 bg-accent-light align-middle" style={{ height: "0.85em" }} />
              </h1>
              {category.subtitle && (
                <p className="mt-3 text-gray-300">{category.subtitle}</p>
              )}

              {icons.length > 0 && (
                <div className="mt-5 flex flex-wrap gap-3">
                  {icons.map((key) => {
                    const entry = DEV_TOOL_ICON_MAP[key];
                    if (!entry) return null;
                    const { Icon, label } = entry;
                    return (
                      <span
                        key={key}
                        title={label}
                        className="flex items-center gap-2 px-3 py-2 rounded-md bg-white/5 border border-navy-border text-white/80 text-sm"
                      >
                        <Icon size={16} />
                        {label}
                      </span>
                    );
                  })}
                </div>
              )}

              {tools.length > 0 && (
                <div className="mt-6">
                  <p className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-3">
                    Choose a Tool
                  </p>
                  <div className="flex flex-wrap gap-3">
                    {tools.map((tool, i) => {
                      const active = tool.id === selectedId;
                      return (
                        <motion.button
                          key={tool.id}
                          type="button"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3, delay: i * 0.05 }}
                          whileHover={{ scale: 1.03 }}
                          whileTap={{ scale: 0.97 }}
                          onClick={() => setSelectedId(tool.id)}
                          className={`px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                            active
                              ? "bg-brand-gradient text-white shadow-md shadow-accent/20"
                              : "bg-white/5 border border-navy-border text-gray-200 hover:border-accent-light hover:text-accent-light"
                          }`}
                        >
                          {tool.title}
                        </motion.button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Bottom container: the selected tool's generator - built ones render here, the rest
          stay an empty workspace reserved for later. */}
      <section className="relative bg-navy-dark text-white min-h-[280px]" data-tool-workspace={selectedTool?.slug || ""}>
        {(() => {
          const Workspace = selectedTool ? TOOL_WORKSPACES[selectedTool.slug] : null;
          return Workspace ? <Workspace key={selectedTool.slug} /> : null;
        })()}
      </section>
    </>
  );
}
